// @ai-generated 2026-07-28T15:01:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import { useToolbarElementsWithout } from '@Components/Tables';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { initialRequisitionSorting, portalRequisitionColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useRecentRequisitions } from '@Hooks/Orders';
import { usePortalServiceLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { type ApiRequisition } from '@Model';
import { requisitionMaterials } from '@utils/requisitionDisplay';
import {
    Button,
    OnboardingPanel,
    RadioCardGroup,
    type ListRequest,
    TableWithStoredSettings,
    type ToolbarOptions,
    useTableRefetch,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { IconTruck } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import classes from './PortalRequisitionList.module.css';
import { PORTAL_ORDER_STEPS } from './portalOrderSteps';
import {
    getPortalRequisitionFilterExpression,
    portalRequisitionFilterOptions,
    type PortalRequisitionFilter,
} from './portalRequisitionFilters';
import { useListApi } from '@Hooks/useListApi';
import { RequisitionsRepository } from '@Repositories/Auftragsbearbeitung';

const HIDE_ADD: ToolbarOptions[] = ['add'];

/**
 * The customer's own order list. Not using the layout-settings table on purpose: the portal
 * columns differ from the ERP ones, and a shared stored layout would overwrite the ERP's for
 * users who work in both modes.
 */
export function PortalRequisitionList() {
    const i18n = useTranslate('');
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const [activeFilter, setActiveFilter] = useState<PortalRequisitionFilter>('all');

    const canCreate = hasPermission(Permission.AuftragsbearbeitungRequisitionsCreate);
    // Without the permission the add button would only ever be a locked button.
    const toolbarElements = useToolbarElementsWithout(undefined, canCreate ? undefined : HIDE_ADD);

    const { refetchRef, refetchTable } = useTableRefetch();
    useRemoteCacheInvalidation('requisitions', refetchTable);

    const { loadPage } = useListApi(RequisitionsRepository, AUFTRAG_API, ['Site', 'Positions', 'ContainerActions']);

    const { ensure: ensureMaterialNames, labelOf: materialOf } = usePortalServiceLabels();
    const loadRequisitions = useCallback(
        async (request: ListRequest) => {
            const page = await loadPage(request);
            ensureMaterialNames(page.items.flatMap(requisitionMaterials));

            return page;
        },
        [loadPage, ensureMaterialNames],
    );

    const columns = useMemo(() => portalRequisitionColumns(materialOf), [materialOf]);

    // Which empty state belongs here cannot be read off the filtered table.
    const { requisitions: firstRequisition } = useRecentRequisitions(1, true);

    const additionalColumnFilters = useMemo(() => getPortalRequisitionFilterExpression(activeFilter), [activeFilter]);

    const startOrder = useCallback(() => navigate('../new'), [navigate]);
    const resetFilter = useCallback(() => setActiveFilter('all'), []);

    // Only the filtered-to-nothing case is a fallback inside the table. Until the probe above has
    // answered there is none at all: a table that came back empty first would otherwise flash
    // "nothing found" at a customer who has simply never ordered.
    const emptyFallback = useMemo(() => {
        if (firstRequisition === undefined) {
            return undefined;
        }

        return (
            <div className={classes.noMatches}>
                <span className={classes.noMatchesTitle}>{i18n('Keine Bestellungen gefunden')}</span>
                <span className={classes.noMatchesHint}>
                    {i18n('Passen Sie Suche oder Filter an, um Bestellungen zu sehen.')}
                </span>
                {activeFilter !== 'all' && (
                    <Button appearance="transparent" onClick={resetFilter}>
                        {i18n('Filter zurücksetzen')}
                    </Button>
                )}
            </div>
        );
    }, [firstRequisition, activeFilter, resetFilter, i18n]);

    // Nothing ordered yet, ever: the onboarding replaces the table instead of sitting inside it,
    // because a toolbar offering search, filters and sorting over an empty list is only noise.
    // An undefined read has not answered yet, or failed, and keeps the table.
    if (firstRequisition?.length === 0) {
        return (
            <OnboardingPanel
                icon={<IconTruck size={42} stroke={1.6} />}
                title={i18n('Noch keine Bestellungen')}
                description={i18n(
                    'Hier sehen Sie alle Ihre Containerbestellungen und deren aktuellen Status. Starten Sie jetzt mit Ihrer ersten Bestellung — es dauert nur wenige Minuten.',
                )}
                action={
                    canCreate ? (
                        <Button type="rounded" onClick={startOrder}>
                            {i18n('Container bestellen')}
                        </Button>
                    ) : undefined
                }
                steps={PORTAL_ORDER_STEPS}
                stepsLabel={i18n('Bestellschritte')}
            />
        );
    }

    return (
        <TableWithStoredSettings<ApiRequisition>
            name="PortalRequisitionsList"
            columns={columns}
            loader={loadRequisitions}
            refetchRef={refetchRef}
            additionalColumnFilters={additionalColumnFilters}
            canCreate={canCreate}
            addLabel={i18n('Container bestellen')}
            toolbarElements={toolbarElements}
            useOnClickNavigation
            initialSorting={initialRequisitionSorting}
            emptyFallback={emptyFallback}
            secondaryToolbar={
                <RadioCardGroup<PortalRequisitionFilter>
                    name="portal-requisition-filter"
                    label={i18n('Bestellungen filtern')}
                    layout="chip"
                    options={portalRequisitionFilterOptions}
                    value={activeFilter}
                    onChange={(value) => {
                        setActiveFilter(value);
                        return true;
                    }}
                />
            }
            fullSize
        />
    );
}
